import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  HelpCircle, 
  Search,
  ShoppingCart,
  Package,
  CreditCard,
  RotateCcw,
  Truck,
  User,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  Mail,
  FileText,
  Hammer,
  Heart,
  Star,
  Settings,
  Lock,
  Bell,
  Camera,
  Gift
} from 'lucide-react';

// Help categories
const HELP_CATEGORIES = [
  {
    id: 'acquisti',
    title: 'Acquisti e Ordini',
    icon: ShoppingCart,
    color: 'bg-blue-500',
    description: 'Come acquistare, carrello, checkout',
    link: '/come-acquistare'
  },
  {
    id: 'pagamenti',
    title: 'Pagamenti',
    icon: CreditCard,
    color: 'bg-green-500',
    description: 'Metodi di pagamento, sicurezza, fatturazione',
    link: '/pagamenti'
  },
  {
    id: 'spedizioni',
    title: 'Spedizioni',
    icon: Truck,
    color: 'bg-amber-500',
    description: 'Tempi, costi, tracking',
    link: '/spedizioni'
  },
  {
    id: 'resi',
    title: 'Resi e Rimborsi',
    icon: RotateCcw,
    color: 'bg-red-500',
    description: 'Politica resi, come restituire, rimborsi',
    link: '/resi-rimborsi'
  },
  {
    id: 'account',
    title: 'Account',
    icon: User,
    color: 'bg-purple-500',
    description: 'Registrazione, login, impostazioni',
    link: null
  },
  {
    id: 'artigiani',
    title: 'Per Artigiani',
    icon: Hammer,
    color: 'bg-orange-500',
    description: 'Vendere su Rarifex, gestione negozio',
    link: null
  }
];

// Popular FAQs
const POPULAR_FAQS = [
  {
    question: 'Come faccio a tracciare il mio ordine?',
    answer: 'Quando l\'artigiano spedisce il pacco, ricevi una email con il codice di tracking. Puoi anche controllare lo stato nella sezione "I miei ordini" della tua dashboard. Clicca sull\'ordine per vedere tutti i dettagli incluso il link al tracking del corriere.',
    category: 'spedizioni'
  },
  {
    question: 'Posso modificare o annullare un ordine?',
    answer: 'Per prodotti standard non ancora spediti, contatta l\'artigiano tramite la chat per richiedere modifiche o annullamento. Per prodotti personalizzati già in lavorazione, l\'annullamento potrebbe non essere possibile. Agisci il prima possibile dopo l\'ordine.',
    category: 'acquisti'
  },
  {
    question: 'Quando riceverò il rimborso?',
    answer: 'Una volta approvato, il rimborso viene elaborato da Stripe in 1-3 giorni lavorativi. La tua banca poi riaccredita l\'importo in 5-10 giorni lavorativi. Il tempo totale è circa 1-2 settimane.',
    category: 'resi'
  },
  {
    question: 'Come funziona il pagamento protetto?',
    answer: 'Quando paghi, i fondi vengono trattenuti da Stripe in modo sicuro. L\'artigiano riceve il pagamento solo quando confermi di aver ricevuto il prodotto. Questo ti protegge in caso di problemi.',
    category: 'pagamenti'
  },
  {
    question: 'Posso restituire un prodotto personalizzato?',
    answer: 'No, i prodotti realizzati su misura o personalizzati non possono essere restituiti per ripensamento (Art. 59 Codice del Consumo). Tuttavia, se il prodotto è difettoso o non conforme, hai diritto al rimborso.',
    category: 'resi'
  },
  {
    question: 'Come contatto un artigiano?',
    answer: 'Dalla pagina del prodotto o dal profilo dell\'artigiano, clicca su "Contatta". Si aprirà una chat dove puoi scrivere direttamente. Riceverai notifica quando l\'artigiano risponde.',
    category: 'acquisti'
  },
  {
    question: 'Quanto tempo ci vuole per ricevere un ordine?',
    answer: 'Dipende dal prodotto: per articoli già pronti, la spedizione avviene in 1-3 giorni e la consegna in 2-5 giorni lavorativi (Italia). Per prodotti su misura, i tempi sono indicati nella proposta dell\'artigiano.',
    category: 'spedizioni'
  },
  {
    question: 'Come divento artigiano su Rarifex?',
    answer: 'Clicca su "Registrati" e seleziona "Sono un artigiano". Compila il profilo con le informazioni sulla tua attività, carica foto dei tuoi lavori e attendi la verifica del team Rarifex (24-48 ore).',
    category: 'artigiani'
  }
];

// FAQ by category
const FAQ_BY_CATEGORY = {
  acquisti: [
    {
      question: 'Come aggiungo un prodotto al carrello?',
      answer: 'Dalla pagina del prodotto, seleziona eventuali varianti (colore, taglia) e clicca "Aggiungi al carrello". Puoi continuare a navigare e aggiungere altri prodotti, anche da artigiani diversi.'
    },
    {
      question: 'Posso acquistare da più artigiani insieme?',
      answer: 'Sì! Puoi aggiungere prodotti di diversi artigiani al carrello e pagare tutto insieme. Ogni artigiano spedirà separatamente la propria parte dell\'ordine.'
    },
    {
      question: 'Come funzionano le richieste personalizzate?',
      answer: 'Puoi creare una richiesta descrivendo cosa desideri. Gli artigiani interessati ti invieranno proposte con prezzo e tempi. Scegli quella che preferisci e procedi al pagamento.'
    },
    {
      question: 'Come applico un codice sconto?',
      answer: 'Nel carrello, trovi un campo "Codice sconto". Inserisci il codice e clicca "Applica". Lo sconto verrà detratto dal totale se il codice è valido e applicabile ai prodotti nel carrello.'
    }
  ],
  pagamenti: [
    {
      question: 'Quali metodi di pagamento accettate?',
      answer: 'Accettiamo carte di credito e debito (Visa, Mastercard, American Express), Apple Pay, Google Pay e bonifico SEPA per ordini di importo elevato.'
    },
    {
      question: 'I miei dati di pagamento sono sicuri?',
      answer: 'Assolutamente. I pagamenti sono gestiti da Stripe, leader mondiale nei pagamenti online. Rarifex non vede mai e non memorizza i dati della tua carta.'
    },
    {
      question: 'Posso pagare a rate?',
      answer: 'Al momento non offriamo pagamento rateale diretto. Tuttavia, alcune carte di credito offrono questa opzione autonomamente. Verifica con la tua banca.'
    },
    {
      question: 'Come ricevo la fattura?',
      answer: 'La fattura viene generata automaticamente dall\'artigiano e inviata via email dopo il pagamento. Puoi anche scaricarla dalla sezione "I miei ordini".'
    }
  ],
  spedizioni: [
    {
      question: 'Spedite all\'estero?',
      answer: 'Dipende dall\'artigiano. Molti spediscono in tutta Europa, alcuni anche nel resto del mondo. Verifica nella scheda prodotto o nel profilo dell\'artigiano.'
    },
    {
      question: 'Posso cambiare l\'indirizzo di spedizione?',
      answer: 'Se l\'ordine non è ancora stato spedito, contatta l\'artigiano tramite la chat per richiedere la modifica. Una volta spedito, non è più possibile cambiare indirizzo.'
    },
    {
      question: 'Cosa faccio se il pacco non arriva?',
      answer: 'Controlla prima il tracking per verificare lo stato. Se risulta consegnato ma non l\'hai ricevuto, contatta il corriere. Se il pacco è perso, contatta l\'artigiano e il supporto Rarifex.'
    },
    {
      question: 'Posso ritirare di persona?',
      answer: 'Alcuni artigiani offrono il ritiro presso la loro bottega. Contatta l\'artigiano prima dell\'ordine per verificare questa possibilità e concordare i dettagli.'
    }
  ],
  resi: [
    {
      question: 'Entro quanto tempo posso restituire?',
      answer: 'Hai 14 giorni dalla ricezione per esercitare il diritto di recesso sui prodotti standard. Per difetti o non conformità, puoi segnalare entro 7 giorni (prima di confermare la ricezione).'
    },
    {
      question: 'Chi paga le spese di reso?',
      answer: 'Se il prodotto è difettoso o non conforme, le spese sono a carico dell\'artigiano. Per resi per ripensamento, le spese sono generalmente a tuo carico.'
    },
    {
      question: 'Come imballo il prodotto per il reso?',
      answer: 'Usa preferibilmente l\'imballaggio originale. Se non disponibile, usa una scatola robusta con materiale protettivo. Includi una copia della conferma d\'ordine.'
    },
    {
      question: 'Posso cambiare taglia/colore invece di restituire?',
      answer: 'Gli scambi diretti non sono gestiti dalla piattaforma. Richiedi il reso del prodotto originale e fai un nuovo ordine per la variante desiderata.'
    }
  ],
  account: [
    {
      question: 'Come creo un account?',
      answer: 'Clicca su "Registrati" in alto a destra. Puoi registrarti con email e password oppure con Google. Conferma l\'email per attivare l\'account.'
    },
    {
      question: 'Ho dimenticato la password, come la recupero?',
      answer: 'Nella pagina di login, clicca "Password dimenticata". Inserisci la tua email e riceverai un link per reimpostare la password.'
    },
    {
      question: 'Come modifico i miei dati personali?',
      answer: 'Accedi alla tua dashboard e vai su "Impostazioni" o "Profilo". Da lì puoi modificare nome, email, indirizzo di spedizione predefinito e altre preferenze.'
    },
    {
      question: 'Come elimino il mio account?',
      answer: 'Contatta il supporto tramite il form di contatto richiedendo la cancellazione dell\'account. Verranno eliminati tutti i tuoi dati personali entro 30 giorni.'
    }
  ],
  artigiani: [
    {
      question: 'Quanto costa vendere su Rarifex?',
      answer: 'La registrazione è gratuita. Rarifex trattiene una commissione sulle vendite completate. Non ci sono costi fissi mensili per il piano base.'
    },
    {
      question: 'Come vengo pagato?',
      answer: 'Quando il cliente conferma la ricezione, il pagamento viene trasferito sul tuo conto Stripe collegato. I tempi di accredito dipendono dalla tua banca (1-3 giorni lavorativi).'
    },
    {
      question: 'Come gestisco le spedizioni?',
      answer: 'Sei responsabile dell\'imballaggio e della spedizione. Puoi scegliere il corriere che preferisci. Ricordati di inserire il codice tracking per permettere al cliente di seguire il pacco.'
    },
    {
      question: 'Posso rifiutare un ordine o una richiesta?',
      answer: 'Sì, puoi rifiutare ordini o richieste se non puoi soddisfarli. Comunica sempre il motivo al cliente. Troppi rifiuti potrebbero influire sulla tua visibilità.'
    }
  ]
};

// Quick links
const QUICK_LINKS = [
  { title: 'Come Funziona', href: '/come-funziona', icon: HelpCircle },
  { title: 'Come Acquistare', href: '/come-acquistare', icon: ShoppingCart },
  { title: 'Prodotto su Misura', href: '/prodotto-su-misura', icon: Sparkles },
  { title: 'Pagamenti Sicuri', href: '/pagamenti', icon: Shield },
  { title: 'Spedizioni', href: '/spedizioni', icon: Truck },
  { title: 'Resi e Rimborsi', href: '/resi-rimborsi', icon: RotateCcw }
];

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  // Filter FAQs based on search
  const filteredFaqs = searchQuery.trim() 
    ? POPULAR_FAQS.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : POPULAR_FAQS;

  return (
    <>
      <Helmet>
        <title>Centro Assistenza | Rarifex - Aiuto e Supporto</title>
        <meta name="description" content="Trova risposte alle tue domande su Rarifex. Guide su acquisti, pagamenti, spedizioni, resi e molto altro. Assistenza clienti per il marketplace dell'artigianato italiano." />
        <meta name="keywords" content="assistenza rarifex, aiuto rarifex, faq rarifex, supporto clienti, domande frequenti, guida acquisti" />
        <link rel="canonical" href="https://www.rarifex.com/assistenza" />
        <meta property="og:title" content="Centro Assistenza | Rarifex" />
        <meta property="og:description" content="Trova risposte alle tue domande. Guide complete su acquisti, pagamenti, spedizioni e resi." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/assistenza" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-br from-[#008080] to-[#006666]">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '20px 20px'}} />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HelpCircle className="w-4 h-4" />
                Centro Assistenza
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
                Come possiamo aiutarti?
              </h1>
              
              <p className="text-xl text-white/80 mb-8">
                Cerca tra le domande frequenti o esplora le nostre guide.
              </p>

              {/* Search bar */}
              <div className="max-w-xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cerca una domanda..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-6 text-lg rounded-xl border-0 shadow-lg"
                  data-testid="help-search-input"
                />
              </div>
            </div>
          </div>
        </section>

        {/* QUICK LINKS */}
        <section className="py-8 bg-white border-b border-slate-100">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-wrap justify-center gap-3">
                {QUICK_LINKS.map((link, index) => (
                  <Link 
                    key={index}
                    to={link.href}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-medium text-slate-700 transition-colors"
                  >
                    <link.icon className="w-4 h-4 text-[#008080]" />
                    {link.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
                Esplora per categoria
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {HELP_CATEGORIES.map((category) => (
                  <Card 
                    key={category.id}
                    className={`p-5 cursor-pointer transition-all hover:shadow-lg ${
                      activeCategory === category.id ? 'ring-2 ring-[#008080] bg-[#008080]/5' : ''
                    }`}
                    onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
                  >
                    <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center mb-4`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{category.title}</h3>
                    <p className="text-sm text-slate-500">{category.description}</p>
                    {category.link && (
                      <Link 
                        to={category.link}
                        className="inline-flex items-center gap-1 text-sm text-[#008080] hover:underline mt-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Guida completa <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY FAQS (when category selected) */}
        {activeCategory && FAQ_BY_CATEGORY[activeCategory] && (
          <section className="py-8 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900">
                    {HELP_CATEGORIES.find(c => c.id === activeCategory)?.title}
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setActiveCategory(null)}
                  >
                    Chiudi
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {FAQ_BY_CATEGORY[activeCategory].map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === `${activeCategory}-${index}` ? null : `${activeCategory}-${index}`)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                        <ChevronDown 
                          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                            expandedFaq === `${activeCategory}-${index}` ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {expandedFaq === `${activeCategory}-${index}` && (
                        <div className="px-5 pb-4">
                          <p className="text-slate-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* POPULAR FAQS */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                {searchQuery ? 'Risultati della ricerca' : 'Domande frequenti'}
              </h2>
              {!searchQuery && (
                <p className="text-slate-600 text-center mb-8">
                  Le risposte alle domande più comuni
                </p>
              )}

              {filteredFaqs.length === 0 ? (
                <div className="text-center py-12">
                  <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Nessun risultato trovato per "{searchQuery}"</p>
                  <Button variant="outline" onClick={() => setSearchQuery('')}>
                    Mostra tutte le FAQ
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFaqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === `popular-${index}` ? null : `popular-${index}`)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                        <ChevronDown 
                          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                            expandedFaq === `popular-${index}` ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {expandedFaq === `popular-${index}` && (
                        <div className="px-5 pb-4">
                          <p className="text-slate-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* STILL NEED HELP */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Non hai trovato quello che cerchi?
                </h2>
                <p className="text-slate-300">
                  Il nostro team è qui per aiutarti.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 bg-white/5 border-white/10 backdrop-blur">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#008080] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2">Scrivici</h3>
                      <p className="text-sm text-slate-400 mb-4">
                        Compila il form di contatto e ti risponderemo entro 24-48 ore lavorative.
                      </p>
                      <Link to="/contatti">
                        <Button className="bg-white text-slate-900 hover:bg-slate-100">
                          Contattaci
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-white/5 border-white/10 backdrop-blur">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DAA520] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2">Parla con un artigiano</h3>
                      <p className="text-sm text-slate-400 mb-4">
                        Per domande su prodotti specifici, contatta direttamente l'artigiano dalla pagina prodotto.
                      </p>
                      <Link to="/products">
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                          Esplora prodotti
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* GUIDES SECTION */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
                Guide complete
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/come-funziona">
                  <Card className="p-6 h-full hover:shadow-lg transition-all group">
                    <FileText className="w-10 h-10 text-[#008080] mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-[#008080]">
                      Come Funziona Rarifex
                    </h3>
                    <p className="text-sm text-slate-600">
                      Scopri come funziona il marketplace, cosa ci rende unici e come iniziare.
                    </p>
                  </Card>
                </Link>

                <Link to="/come-acquistare">
                  <Card className="p-6 h-full hover:shadow-lg transition-all group">
                    <ShoppingCart className="w-10 h-10 text-blue-500 mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-blue-500">
                      Guida all'Acquisto
                    </h3>
                    <p className="text-sm text-slate-600">
                      Tutto quello che devi sapere per fare acquisti sicuri su Rarifex.
                    </p>
                  </Card>
                </Link>

                <Link to="/prodotto-su-misura">
                  <Card className="p-6 h-full hover:shadow-lg transition-all group">
                    <Sparkles className="w-10 h-10 text-amber-500 mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-amber-500">
                      Prodotti su Misura
                    </h3>
                    <p className="text-sm text-slate-600">
                      Come richiedere creazioni personalizzate direttamente agli artigiani.
                    </p>
                  </Card>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
